import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Building2, Users, DollarSign, TrendingUp, Package, FileText, Globe } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const MasterDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      const response = await api.get('/master-dashboard');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching master dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
          <p className="text-slate-500 mt-2">Only Super Admins can access this dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="master-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Master Dashboard</h1>
          <p className="text-slate-500 mt-1">Consolidated view across all companies</p>
        </div>
        <Badge variant="outline" className="text-teal-700 border-teal-300 bg-teal-50">
          <Globe className="h-3 w-3 mr-1" />
          All Companies
        </Badge>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Companies</p>
                <p className="text-2xl font-bold text-slate-900">{data?.total_companies || 0}</p>
              </div>
              <div className="h-10 w-10 bg-teal-100 rounded-full flex items-center justify-center">
                <Building2 className="h-5 w-5 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Users</p>
                <p className="text-2xl font-bold text-slate-900">{data?.total_users || 0}</p>
              </div>
              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900">
                  ${(data?.total_revenue || 0).toLocaleString()}
                </p>
              </div>
              <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Clients</p>
                <p className="text-2xl font-bold text-slate-900">{data?.total_clients || 0}</p>
              </div>
              <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-teal-600" />
            Company Performance Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Company</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Country</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Clients</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Invoices</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Revenue</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Expenses</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Profit</th>
                </tr>
              </thead>
              <tbody>
                {data?.companies?.map((company) => (
                  <tr key={company.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{company.code}</Badge>
                        <span className="font-medium text-slate-900">{company.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{company.country}</td>
                    <td className="py-3 px-4 text-right text-slate-900">{company.clients}</td>
                    <td className="py-3 px-4 text-right text-slate-900">{company.invoices}</td>
                    <td className="py-3 px-4 text-right text-green-600 font-medium">
                      ${(company.revenue || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right text-red-600">
                      ${(company.expenses || 0).toLocaleString()}
                    </td>
                    <td className={`py-3 px-4 text-right font-semibold ${
                      (company.revenue - company.expenses) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ${((company.revenue || 0) - (company.expenses || 0)).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td className="py-3 px-4 text-slate-900">Total</td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4 text-right text-slate-900">{data?.total_clients || 0}</td>
                  <td className="py-3 px-4 text-right text-slate-900">{data?.total_invoices || 0}</td>
                  <td className="py-3 px-4 text-right text-green-600">
                    ${(data?.total_revenue || 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-red-600">
                    ${(data?.total_expenses || 0).toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-right ${
                    ((data?.total_revenue || 0) - (data?.total_expenses || 0)) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ${((data?.total_revenue || 0) - (data?.total_expenses || 0)).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">{data?.total_projects || 0}</p>
            <p className="text-sm text-slate-500 mt-1">Across all companies</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Employees</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">{data?.total_employees || 0}</p>
            <p className="text-sm text-slate-500 mt-1">Across all companies</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Products</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">{data?.total_products || 0}</p>
            <p className="text-sm text-slate-500 mt-1">Across all companies</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MasterDashboard;
